import {
  ArrowsClockwiseIcon,
  CalendarDotsIcon,
  CalendarPlusIcon,
  CalendarXIcon,
  ChatTeardropTextIcon,
  ClipboardTextIcon,
  FunnelIcon,
  HandTapIcon,
  type Icon,
  PaperPlaneTiltIcon,
  NotePencilIcon,
  PhoneIcon,
  PhoneSlashIcon,
  PulseIcon,
  ToothIcon,
  UserGearIcon,
  UserPlusIcon,
} from '@phosphor-icons/react'
import { comActivityTypes } from 'lib/comActivityTypes'
import { listCopy, type ListCopy } from 'lib/listCopy'
import type { Condition, ConditionMatch } from './conditions'
import { TREATMENT_MILESTONES, treatmentMilestoneForEvent, type TreatmentTriggerData } from './treatmentTrigger'

// The trigger catalog mirrors the app's REAL objects: a trigger is "when an {object} is Created or
// Updated", and the object decides which properties can be filtered. What a practice calls a
// distinct trigger (missed call, no-show) is a property filter on one of those events, surfaced as a
// preset. Objects the engine can't emit yet stay authorable but are gated from Activation, so the UI
// leads and the gap is flagged rather than removed.

/** The engine's actual lifecycle events (1:1 with WorkflowTriggerEvent). Since the event-stream
 *  pivot (PR #366 / TRK-5397) plus PR #371, Contact, Call, Form, Activity, and Message are all
 *  Event-backed sources. Message events are creation-only (messages are immutable); Activity events
 *  exclude the Call / Form / Change types, which have their own sources. */
export type TriggerEngineEvent =
  | `contact_created`
  | `contact_updated`
  | `call_created`
  | `call_updated`
  | `call_finished_processing`
  | `appointment_date_reached`
  | `contact_date_reached`
  | `form_created`
  | `form_updated`
  | `activity_created`
  | `activity_updated`
  | `message_created`
  | `task_created`
  | `task_updated`
  | `appointment_created`
  | `appointment_updated`
  | `treatment_plan_updated`
  | `paperwork_completed`

/** The core objects a workflow can trigger on — the app's real top-level models, plus `manual`
 *  (no object: contacts enter only when someone pushes them in). */
export type TriggerObject =
  | `contact`
  | `form`
  | `call`
  | `activity`
  | `message`
  | `appointment`
  | `treatment_plan`
  | `comm_log`
  | `task`
  | `paperwork`
  | `manual`

/** Every object supports the same two lifecycle moments. */
export type TriggerLifecycle = `created` | `updated`

/** A trigger kind is one object × one lifecycle — plus `manual`, the only non-event kind. */
/** Every object gets `<object>_<lifecycle>`, plus two that do not fit that grid: `manual` (no object
 *  and no moment) and `call_finished_processing` (a synthetic event the API emits when a call's
 *  pipeline completes, which is neither the call being created nor a field on it changing). */
export type TriggerKind =
  | `${Exclude<TriggerObject, `manual` | `paperwork`>}_${TriggerLifecycle}`
  | `manual`
  | `call_finished_processing`
  | `appointment_date_reached`
  | `contact_date_reached`
  | `paperwork_completed`
  | `message_thread_created`

/** The first inbound text on a new thread. Builder steps read this to reply to the sender and to
 *  say plainly that contact steps skip when the sender is not a contact yet. */
export const NEW_TEXT_CONVERSATION = `message_thread_created` as const

/** The subject a workflow run is about — drives which object (contact vs activity) is in scope. */
export type TriggerSubject = `contact` | `activity`

/** Who/what caused the change that fires the trigger. The Contact chokepoint fires on machine writes
 *  too (syncs, recompute), so state/change triggers default to `user` to avoid firing on robot storms.
 *  See the contact-state audit. Enforced via the change-activity `source` field (backend). */
export type TriggerSource = `user` | `system` | `any`

/** `live` = the engine can run it today. `backend` = authorable now, but blocked from Activate until
 *  the engine supports it (see `backendNote` for what's missing). */
export type TriggerStatus = `live` | `backend`

/** How reliably this trigger puts a contact in scope, driving contact-targeting validation:
 *  - `always`      — the contact is the subject (contact / appointment / task events).
 *  - `conditional` — may not resolve to a contact (a call from an unknown number). */
export type ContactProvision = `always` | `conditional`

/** A preset filter the engine always applies for this trigger (e.g. a Call event is really
 *  "activity created where type = Call"). Uses the engine's filter-operator vocabulary. */
export type TriggerPresetFilter = { attribute: string; operator: string; value: string }

export type TriggerDef = {
  kind: TriggerKind
  object: TriggerObject
  lifecycle: TriggerLifecycle
  /** The object's display name ("Call", "Appointment") — groups the picker. */
  objectLabel: string
  /** Full trigger label ("Call received", "Appointment created"). */
  label: string
  /** Shown under the title in the picker and panel. */
  summary: string
  icon: Icon
  /** The engine event this compiles to. */
  engineEvent: TriggerEngineEvent
  /** Filters the engine always applies for this trigger (before the user's property conditions). */
  presetFilters?: TriggerPresetFilter[]
  contactProvision: ContactProvision
  status: TriggerStatus
  /** For `backend` triggers: what the engine must add before this can go live. Feeds the gap message. */
  backendNote?: string
  /** Kept out of the trigger picker (superseded or won't-do), while old saves still deserialize,
   *  render, and validate against this def. */
  pickerHidden?: boolean
  /** The generic Activity triggers let the user pick which activity type fires them. */
  configurableActivityType?: boolean
  /** The object's "Any ..." event, listed last in its picker group. */
  catchAll?: boolean
  /** A blunt state-only trigger (fires on any save) — surfaces the state-vs-event advisory. */
  blunt?: boolean
  /** Default "Triggered by" for source-filterable triggers. Contact updates default to `user` (staff
   *  changes) since the chokepoint fires on machine writes too; created events default to `any`. */
  defaultSource?: TriggerSource
}

// Objects in picker order — everyday events first (contact, form, call), manual enrollment last as
// its own explicit group.
export const TRIGGER_OBJECT_ORDER: TriggerObject[] = [
  `contact`,
  `form`,
  `call`,
  `activity`,
  `message`,
  `appointment`,
  `treatment_plan`,
  `comm_log`,
  `task`,
  `paperwork`,
  `manual`,
]

export const TRIGGER_OBJECT_LABELS: Record<TriggerObject, string> = {
  contact: `Contact`,
  form: `Form`,
  call: `Call`,
  activity: `Activity`,
  message: `Message`,
  appointment: `Appointment`,
  treatment_plan: `Treatment plan`,
  comm_log: `PMS comm log`,
  task: `Task`,
  paperwork: `Paperwork`,
  manual: `Manual`,
}

export const TRIGGER_CATALOG: TriggerDef[] = [
  // — Contact — the state lane. "Updated" carries the lifecycle/status moments as property filters.
  {
    kind: `contact_created`,
    object: `contact`,
    lifecycle: `created`,
    objectLabel: `Contact`,
    label: `Contact created`,
    summary: `A new contact is added`,
    icon: UserPlusIcon,
    engineEvent: `contact_created`,
    contactProvision: `always`,
    status: `live`,
  },
  {
    kind: `contact_updated`,
    object: `contact`,
    lifecycle: `updated`,
    objectLabel: `Contact`,
    label: `Any contact change`,
    catchAll: true,
    summary: `A contact record changes (status, owner, tags…)`,
    icon: UserGearIcon,
    engineEvent: `contact_updated`,
    contactProvision: `always`,
    status: `live`,
    blunt: true,
    defaultSource: `user`,
  },

  // — Form — first-class Event-backed source since the event-stream pivot (stable Form records,
  // not the churny Form activities). The evaluator resolves the contact via the Form record.
  {
    kind: `form_created`,
    object: `form`,
    lifecycle: `created`,
    objectLabel: `Form`,
    label: `Form submitted`,
    summary: `A contact submits a form`,
    icon: NotePencilIcon,
    engineEvent: `form_created`,
    contactProvision: `conditional`,
    status: `live`,
  },
  {
    kind: `form_updated`,
    object: `form`,
    lifecycle: `updated`,
    objectLabel: `Form`,
    label: `Any form change`,
    catchAll: true,
    summary: `A form record changes`,
    icon: ArrowsClockwiseIcon,
    engineEvent: `form_updated`,
    contactProvision: `conditional`,
    status: `live`,
  },

  {
    // Date-anchored triggers count from a date already on the record, so one workflow covers a whole
    // ladder: each offset fires independently for the same appointment (trackable-api #524). The
    // engine re-reads the record at due time, which is why a cancelled or rescheduled appointment
    // resolves itself without any stored timer to clean up.
    kind: `appointment_date_reached`,
    object: `appointment`,
    lifecycle: `updated`,
    objectLabel: `Appointment`,
    label: `Before or after an appointment`,
    summary: `A set time before or after an appointment, for reminders and follow-ups`,
    icon: CalendarDotsIcon,
    engineEvent: `appointment_date_reached`,
    contactProvision: `always`,
    status: `live`,
  },
  {
    kind: `contact_date_reached`,
    object: `contact`,
    lifecycle: `updated`,
    objectLabel: `Contact`,
    label: `On a date on the contact`,
    summary: `A set time before or after a date the contact carries, like their last hygiene visit`,
    icon: CalendarDotsIcon,
    engineEvent: `contact_date_reached`,
    contactProvision: `always`,
    status: `live`,
  },
  // — Call — first-class Event-backed source (the Call record, not the Call activity).
  // `answered` / `direction` are Call properties written at create and never touched again; see
  // serialize.ts for why a "just changed" check on them only works on Call received.
  {
    kind: `call_created`,
    object: `call`,
    lifecycle: `created`,
    objectLabel: `Call`,
    label: `Any call`,
    catchAll: true,
    summary: `A call comes in or is logged`,
    icon: PhoneIcon,
    engineEvent: `call_created`,
    contactProvision: `conditional`,
    status: `live`,
  },
  {
    kind: `call_updated`,
    object: `call`,
    lifecycle: `updated`,
    objectLabel: `Call`,
    // Superseded by `call_finished_processing`, which fires at the same real-world moment (the one
    // update a call ever emits: transcription + AI extraction landing) but also guarantees the
    // caller has been matched to a contact. Two picker rows named for the same moment confused
    // more than they offered, so this one is hidden from the picker; existing saves keep working.
    label: `Call analyzed`,
    summary: `The AI finishes reviewing a call and sets its outcome`,
    icon: PhoneIcon,
    engineEvent: `call_updated`,
    contactProvision: `conditional`,
    status: `live`,
    pickerHidden: true,
  },
  {
    // The moment everything about a call is finally true: transcription and AI extraction have run,
    // the outcome and tags are written, and the caller has been matched to a contact. Emitted as its
    // own synthetic event by the API (trackable-api #506) precisely because no existing event marked
    // it: a call arrives with nothing decided, and "updated" fires whenever any field moves.
    kind: `call_finished_processing`,
    object: `call`,
    lifecycle: `updated`,
    objectLabel: `Call`,
    label: `Call analyzed`,
    summary: `A call has been transcribed and reviewed, and its outcome is known`,
    icon: PhoneIcon,
    engineEvent: `call_finished_processing`,
    contactProvision: `conditional`,
    // Live. Enrollment proven twice on real inbound calls, and the timestamp bug that made a freshly
    // activated workflow blind to them (TRK-5552) is fixed and QA'd: a call placed 72 seconds after
    // activation enrolled. What remains broken is a SEND that addresses the caller's number, which is
    // a step-level problem (TRK-5563) and gated there rather than here.
    status: `live`,
  },

  // — Activity — the generic timeline event. The event stream (v1) records no Activity events, so
  // the engine no longer evaluates these; gated until an Activity event source exists.
  {
    kind: `activity_created`,
    object: `activity`,
    lifecycle: `created`,
    objectLabel: `Activity`,
    label: `Activity created`,
    summary: `Any activity is logged (calls & forms have their own triggers)`,
    icon: PulseIcon,
    engineEvent: `activity_created`,
    contactProvision: `conditional`,
    status: `live`,
    configurableActivityType: true,
  },
  {
    kind: `activity_updated`,
    object: `activity`,
    lifecycle: `updated`,
    objectLabel: `Activity`,
    label: `Any activity change`,
    catchAll: true,
    summary: `An activity record changes`,
    icon: ArrowsClockwiseIcon,
    engineEvent: `activity_updated`,
    contactProvision: `conditional`,
    status: `live`,
    configurableActivityType: true,
  },

  // — Message — the conversation object (SMS/email in the inbox). Event-backed since PR #371:
  // Message.afterSave emits a creation event (messages are immutable, so created is the only
  // lifecycle). Our field keys (messageDirection / messageChannel…) are aliases the serializer maps
  // to the raw Message fields the event data records — see MESSAGE_ENGINE_ATTRIBUTE in serialize.
  {
    kind: `message_created`,
    object: `message`,
    lifecycle: `created`,
    objectLabel: `Message`,
    label: `Any message`,
    catchAll: true,
    summary: `A message lands in a conversation (either direction)`,
    icon: ChatTeardropTextIcon,
    engineEvent: `message_created`,
    contactProvision: `conditional`,
    status: `live`,
  },
  // Compiles to message_created as a placeholder (as comm_log_created does with contact_updated) so
  // a draft still saves and reopens from builderJson; `backend` blocks Activate until the engine
  // emits the event and the agent can reply in place of an opener.
  {
    kind: NEW_TEXT_CONVERSATION,
    object: `message`,
    lifecycle: `created`,
    objectLabel: `Message`,
    label: `New text conversation`,
    summary: `Someone texts the practice for the first time.`,
    icon: ChatTeardropTextIcon,
    engineEvent: `message_created`,
    contactProvision: `conditional`,
    status: `backend`,
    backendNote: `the engine to emit a new-conversation event and run the AI agent in reply mode (TRK-6227)`,
  },
  {
    // Won't-do, hidden from the picker (was a NEEDS BACKEND badge, which implied "coming"): the
    // only things that change on a landed message are read/archived, and those deliberately emit
    // no events. Kept in the catalog so an old save still deserializes and blocks Activate with
    // the honest note.
    kind: `message_updated`,
    object: `message`,
    lifecycle: `updated`,
    objectLabel: `Message`,
    label: `Message updated`,
    summary: `A message changes (read, archived)`,
    icon: ArrowsClockwiseIcon,
    engineEvent: `contact_updated`,
    contactProvision: `conditional`,
    status: `backend`,
    backendNote: `a message-updated event, which is deliberately never emitted (read/archived changes are inbox noise, not automation moments); trigger on "Message sent or received" instead`,
    pickerHidden: true,
  },

  // — Appointment — event-backed since PR #389 (Appointment.afterSave emits created/updated);
  // fully verified live (TRK-5363 closed 08-10): created leg 08-07, updated leg + the recall
  // trigger shape (status→Completed filter) 08-10. eventDate stamps the change moment (#430),
  // so there are no trigger-window surprises.
  {
    kind: `appointment_created`,
    object: `appointment`,
    lifecycle: `created`,
    objectLabel: `Appointment`,
    label: `Appointment created`,
    summary: `An appointment is booked`,
    icon: CalendarPlusIcon,
    engineEvent: `appointment_created`,
    contactProvision: `always`,
    status: `live`,
  },
  {
    kind: `appointment_updated`,
    object: `appointment`,
    lifecycle: `updated`,
    objectLabel: `Appointment`,
    label: `Any appointment change`,
    catchAll: true,
    summary: `An appointment changes (rescheduled, broken, completed)`,
    icon: CalendarDotsIcon,
    engineEvent: `appointment_updated`,
    contactProvision: `always`,
    status: `live`,
  },

  // One entry, five moments. The API publishes one event per moment (see TREATMENT_MOMENT_EVENTS).
  {
    kind: `treatment_plan_updated`,
    object: `treatment_plan`,
    lifecycle: `updated`,
    objectLabel: `Treatment plan`,
    label: `Any treatment plan change`,
    catchAll: true,
    summary: `Treatment on a patient's plan is presented, booked, completed or closed`,
    icon: ToothIcon,
    engineEvent: `treatment_plan_updated`,
    contactProvision: `always`,
    status: `live`,
  },

  // Compiles to contact_updated as a placeholder (as message_updated does) so a draft still saves;
  // `backend` blocks Activate until the API emits comm_log_created.
  {
    kind: `comm_log_created`,
    object: `comm_log`,
    lifecycle: `created`,
    objectLabel: `PMS comm log`,
    label: `PMS comm log added`,
    summary: `A note lands in the PMS comm log, from staff or another tool`,
    icon: NotePencilIcon,
    engineEvent: `contact_updated`,
    contactProvision: `conditional`,
    status: `backend`,
    backendNote: `the API to start emitting comm log events (TRK-6138)`,
  },

  // — Task — event-backed since PR #396 (verified live 08-06): Task.afterSave emits created/updated
  // events; contact resolution reads the task's contactId (a task may have none → conditional).
  {
    kind: `task_created`,
    object: `task`,
    lifecycle: `created`,
    objectLabel: `Task`,
    label: `Task created`,
    summary: `A task is created`,
    icon: ClipboardTextIcon,
    engineEvent: `task_created`,
    contactProvision: `conditional`,
    status: `live`,
  },
  {
    kind: `task_updated`,
    object: `task`,
    lifecycle: `updated`,
    objectLabel: `Task`,
    label: `Any task change`,
    catchAll: true,
    summary: `A task changes (completed, reassigned…)`,
    icon: ClipboardTextIcon,
    engineEvent: `task_updated`,
    contactProvision: `conditional`,
    status: `live`,
  },

  {
    kind: `paperwork_completed`,
    object: `paperwork`,
    lifecycle: `updated`,
    objectLabel: `Paperwork`,
    label: `Paperwork completed`,
    summary: `A patient finishes a paperwork packet`,
    icon: ClipboardTextIcon,
    engineEvent: `paperwork_completed`,
    contactProvision: `always`,
    status: `live`,
  },

  // — Manual — the only non-event trigger: contacts enter ONLY when someone pushes them in (Runs
  // panel, contacts table, bulk action). Serializes to the never-match convention the engine
  // ignores organically while direct/bulk enrollment pushes past it (verified live 08-12,
  // TRK-5430): engineEvent contact_updated + a filter no contact ever matches. `lifecycle` is
  // inert for this kind.
  {
    kind: `manual`,
    object: `manual`,
    lifecycle: `created`,
    objectLabel: `Manual`,
    label: `Manual enrollment`,
    summary: `People enter only when you add them`,
    icon: HandTapIcon,
    engineEvent: `contact_updated`,
    contactProvision: `always`,
    status: `live`,
  },
]

const CATALOG_BY_KIND: Record<TriggerKind, TriggerDef> = Object.fromEntries(
  TRIGGER_CATALOG.map((def) => [def.kind, def]),
) as Record<TriggerKind, TriggerDef>

export function triggerDef(kind: TriggerKind): TriggerDef {
  return CATALOG_BY_KIND[kind]
}

/** Reverse of `engineEvent`, derived from the catalog so a new trigger deserializes the day it is
 *  added. Hand-maintaining this list is how both date triggers ended up reopening as "Contact
 *  created" and losing their schedule on the next save.
 *
 *  The mapping is not 1:1: `contact_updated` is the engine event for `contact_updated`, for `manual`
 *  (the never-match enrollment convention) and for the retired `message_updated`. Where several
 *  kinds claim one event, the kind NAMED after the event wins, and the conventions that pick out the
 *  others (a manual doc's sentinel filter) are recognized before this lookup is consulted. The
 *  tie-break is order-independent on purpose: resolving by catalog position would silently hand
 *  every Contact-updated workflow to whichever def was appended last. */
const KIND_BY_ENGINE_EVENT = TRIGGER_CATALOG.reduce<Partial<Record<TriggerEngineEvent, TriggerKind>>>((map, def) => {
  if (!map[def.engineEvent] || def.kind === def.engineEvent) map[def.engineEvent] = def.kind
  return map
}, {})

/** An engine event → the trigger kind that owns it, or undefined for an event no trigger claims
 *  (a legacy value, or one the API added before the builder learned it). */
export function kindForEngineEvent(event: string): TriggerKind | undefined {
  return (
    KIND_BY_ENGINE_EVENT[event as TriggerEngineEvent] ??
    (treatmentMilestoneForEvent(event) ? `treatment_plan_updated` : undefined)
  )
}

export function triggerLabel(kind: TriggerKind): string {
  return CATALOG_BY_KIND[kind]?.label ?? `Trigger`
}

export function triggerSummary(kind: TriggerKind): string {
  return CATALOG_BY_KIND[kind]?.summary ?? `When the trigger fires`
}

export function triggerIcon(kind: TriggerKind): Icon {
  return CATALOG_BY_KIND[kind]?.icon ?? PulseIcon
}

export function triggerObject(kind: TriggerKind): TriggerObject {
  return CATALOG_BY_KIND[kind]?.object ?? `contact`
}

export function triggerContactProvision(kind: TriggerKind): ContactProvision {
  return CATALOG_BY_KIND[kind]?.contactProvision ?? `always`
}

/** "appointment" when the trigger fires on another record and always resolves to that record's
 *  contact, so the builder can say whose run it is. Undefined when the contact is the record, or
 *  when there may be no contact at all. */
export function contactSourceNoun(kind: TriggerKind): string | undefined {
  const def = CATALOG_BY_KIND[kind]
  if (!def || def.object === `contact` || def.object === `manual` || def.contactProvision !== `always`) return undefined
  return def.objectLabel.toLowerCase()
}

const RECORD_SUBJECT_EVENTS = new Set<TriggerEngineEvent>([
  `activity_created`,
  `activity_updated`,
  `call_created`,
  `call_updated`,
  `call_finished_processing`,
  `appointment_date_reached`,
  `form_created`,
  `form_updated`,
  `message_created`,
])

/** The run subject (contact vs activity) — activity for record-sourced events (activity/call/form:
 *  the triggering record's fields are in scope), contact otherwise. */
export function triggerSubject(kind: TriggerKind): TriggerSubject {
  const engineEvent = CATALOG_BY_KIND[kind]?.engineEvent
  return engineEvent && RECORD_SUBJECT_EVENTS.has(engineEvent) ? `activity` : `contact`
}

/** Whether this trigger should offer a "Triggered by" source filter. Only contact-save events flow
 *  through the Contact chokepoint (which also fires on machine writes), so only they need it. */
export function triggerSourceFilterable(kind: TriggerKind): boolean {
  const def = CATALOG_BY_KIND[kind]
  // Appointment/Task use contact_updated as a placeholder event but aren't contact-save triggers, so
  // scope the source filter to the actual Contact object.
  return def?.object === `contact` && (def.engineEvent === `contact_created` || def.engineEvent === `contact_updated`)
}

/** The "Triggered by" options, from most to least restrictive. */
export const TRIGGER_SOURCE_OPTIONS: { value: TriggerSource; label: string }[] = [
  { value: `user`, label: `Staff` },
  { value: `system`, label: `System` },
  { value: `any`, label: `Anyone` },
]

/** The effective source for a trigger, applying the catalog default when the user hasn't set one. */
export function triggerEffectiveSource(kind: TriggerKind, source: TriggerSource | undefined): TriggerSource {
  return source ?? CATALOG_BY_KIND[kind]?.defaultSource ?? `any`
}

/** Activity types offered for the generic activity triggers — the app's real list (`comActivityTypes`). */
export const ACTIVITY_TYPES = [`Any activity`, ...comActivityTypes]

const ENGINE_EVENT_LABEL: Record<TriggerEngineEvent, string> = {
  contact_created: `Contact created`,
  contact_updated: `Any contact change`,
  call_created: `Any call`,
  call_updated: `Call changed`,
  call_finished_processing: `Call analyzed`,
  appointment_date_reached: `Before or after an appointment`,
  contact_date_reached: `On a date on the contact`,
  form_created: `Form submitted`,
  form_updated: `Any form change`,
  activity_created: `Activity created`,
  activity_updated: `Any activity change`,
  message_created: `Any message`,
  task_created: `Task created`,
  task_updated: `Any task change`,
  appointment_created: `Appointment created`,
  appointment_updated: `Any appointment change`,
  treatment_plan_updated: `Any treatment plan change`,
  paperwork_completed: `Paperwork completed`,
}

/** Events retired by the event-stream pivot; old remote docs may still carry them. */
const LEGACY_EVENT_LABEL: Record<string, string> = {
  form_submitted: `Form submitted`,
}

/** Label for a raw engine event (used where we only have the engine trigger, e.g. the remote list). */
export function engineEventLabel(engineEvent: string): string {
  const milestone = treatmentMilestoneForEvent(engineEvent)
  if (milestone) return TREATMENT_MILESTONES.find((candidate) => candidate.value === milestone)?.label ?? `Trigger`
  return ENGINE_EVENT_LABEL[engineEvent as TriggerEngineEvent] ?? LEGACY_EVENT_LABEL[engineEvent] ?? `Trigger`
}

/** An engine event as the Event docs store it: a type and an action. Enum values are TYPE_ACTION
 *  (CONTACT_UPDATED), so the split is at the first underscore, except TreatmentPlan, whose type is
 *  two words (TREATMENT_PLAN_PROCEDURE_BOOKED is type "TreatmentPlan", action "procedure_booked"). */
export function engineEventSource(engineEvent: string): { type: string; action: string } | undefined {
  const upper = String(engineEvent).toUpperCase()
  if (upper.startsWith(`TREATMENT_PLAN_`)) {
    // The engine files the closed event under action "plan_closed", not the generic "closed".
    const action = upper === `TREATMENT_PLAN_CLOSED` ? `plan_closed` : upper.slice(`TREATMENT_PLAN_`.length)
    return { type: `TreatmentPlan`, action: action.toLowerCase() }
  }
  const [head, ...rest] = upper.split(`_`)
  if (!head || rest.length === 0) return undefined
  return { type: head.charAt(0) + head.slice(1).toLowerCase(), action: rest.join(`_`).toLowerCase() }
}

/** The label for a stored Event's type and action ("TreatmentPlan", "procedure_booked"): the inverse
 *  of `engineEventSource`. */
export function storedEventLabel(type: string, action: string): string {
  const eventAction = type === `TreatmentPlan` ? action.replace(/^plan_/, ``) : action
  return engineEventLabel(`${type.replace(/([a-z])([A-Z])/g, `$1_$2`).toLowerCase()}_${eventAction}`)
}

/** The record an engine event is about ("treatment plan", "appointment"), for copy that only has
 *  the compiled trigger. An event no catalog kind claims reads as its first word. */
export function engineEventNoun(engineEvent: string): string {
  const kind = kindForEngineEvent(engineEvent.toLowerCase())
  if (kind) return CATALOG_BY_KIND[kind].objectLabel.toLowerCase()
  return engineEvent.split(`_`)[0].toLowerCase() || `trigger`
}

/** Plain-language statement of what the workflow runs on, so the subject (contact vs activity) and the
 *  state-vs-event caveat are explicit at the source. */
export function triggerSubjectLine(kind: TriggerKind): string {
  const def = CATALOG_BY_KIND[kind]
  // "Each ... starts one run", not "runs once per contact": the old wording read as a lifetime
  // promise, and how often a contact can re-enter is the Re-enrollment setting's job to say.
  if (!def) return `Each trigger starts one run for that contact.`
  if (kind === `manual`) return `Runs only when you add someone. Contacts never enter on their own.`
  if (def.blunt)
    return `Runs every time the record is saved, even for edits unrelated to your goal. Add property conditions to narrow it.`
  if (def.contactProvision === `conditional`)
    return `Each matching activity (e.g. a call) starts one run. It may not be linked to a contact.`
  return `Each trigger starts one run for that contact.`
}

/** Triggers grouped by object, in display order — for the picker and the kind dropdown. */
export function triggerCatalogByObject(): { object: TriggerObject; label: string; defs: TriggerDef[] }[] {
  return TRIGGER_OBJECT_ORDER.map((object) => ({
    object,
    label: TRIGGER_OBJECT_LABELS[object],
    defs: TRIGGER_CATALOG.filter((def) => def.object === object && !def.pickerHidden),
  })).filter((group) => group.defs.length > 0)
}

// ————————————————————————————————————————————————————————————————————————————————————————————————
// Presets — named front doors that pre-configure a base object trigger. These are how a practice
// discovers the everyday moments ("Missed call", "No-show") without us promoting them to real triggers.
// A preset just selects a kind and prefills property conditions; it's fully editable afterwards.
// ————————————————————————————————————————————————————————————————————————————————————————————————

/** The subset of trigger node data a preset can prefill (kept structural to avoid a types.ts cycle). */
export type TriggerPresetData = {
  activityType?: string
  conditions?: Condition[]
  match?: ConditionMatch
  /** How the state-scope ("only if") rows combine, when the preset mixes scopes. */
  stateMatch?: ConditionMatch
  source?: TriggerSource
  reenrollment?: `ignore` | `restart` | `allow_multiple`
  requireContactMatch?: boolean
  treatment?: TreatmentTriggerData
}

export type TriggerPreset = {
  id: string
  label: string
  summary: string
  icon: Icon
  kind: TriggerKind
  data: TriggerPresetData
}

const field = (fieldKey: string, value: string): Condition => ({
  type: `field`,
  field: fieldKey,
  operator: `is`,
  value,
})

const buildTriggerPresets = (copy: ListCopy): TriggerPreset[] => [
  {
    id: `form_submitted`,
    label: `Form submitted`,
    summary: `A contact submits a form`,
    icon: NotePencilIcon,
    kind: `form_created`,
    data: {},
  },
  {
    id: `missed_call`,
    label: `Missed call`,
    summary: `A call goes unanswered`,
    icon: PhoneSlashIcon,
    // The exact shape the Missed call text-back template ships (see missedCallTextback() for the
    // 300-call measurement behind it). "Missed" is two facts and either alone gets it wrong: rang-out
    // calls often carry no outcome (nothing recorded to classify), and voicemail pickups read
    // answered:true while the app shows Missed Call. So ANY of the two signals, ONLY IF inbound
    // (ten of 117 Missed Call outcomes sat on OUTBOUND calls). This preset used to build the bare
    // answered:false version on the gated Call received trigger, so the picker path produced a
    // different, wrong machine than the gallery path for the same intent.
    kind: `call_finished_processing`,
    data: {
      conditions: [
        { type: `field`, field: `allPatientOutcomes`, operator: `contains`, value: `Missed Call` },
        { type: `field`, field: `answered`, operator: `is`, value: `false` },
        { type: `field`, field: `direction`, operator: `is`, value: `inbound`, scope: `state` },
      ],
      match: `any`,
      stateMatch: `all`,
      reenrollment: `allow_multiple`,
      requireContactMatch: false,
    },
  },
  {
    id: `appointment_booked`,
    label: `Appointment booked`,
    summary: `A new appointment is scheduled`,
    icon: CalendarPlusIcon,
    kind: `appointment_created`,
    data: {},
  },
  {
    id: `no_show`,
    label: `Broken appointment`,
    summary: `An appointment is marked broken (no-show or late cancel)`,
    icon: CalendarXIcon,
    kind: `appointment_updated`,
    data: { conditions: [field(`status`, `Broken`)], match: `all` },
  },
  {
    id: `became_patient`,
    label: `Became a patient`,
    summary: `A lead converts to an active patient`,
    icon: UserPlusIcon,
    kind: `contact_updated`,
    data: { conditions: [field(`patientStatus`, `Active`)], match: `all`, source: `any` },
  },
  {
    id: `became_inactive`,
    label: `Became inactive`,
    summary: `A patient goes inactive (reactivation)`,
    icon: UserGearIcon,
    kind: `contact_updated`,
    data: { conditions: [field(`patientStatus`, `Inactive`)], match: `all`, source: `any` },
  },
  {
    id: `closed_lost`,
    label: `Lead closed lost`,
    summary: `A lead is marked closed-lost (win-back)`,
    icon: UserGearIcon,
    kind: `contact_updated`,
    data: { conditions: [field(`leadStatus`, `Closed Lost`)], match: `all`, source: `any` },
  },
  {
    id: `sms_received`,
    label: `SMS received`,
    summary: `A contact texts the practice`,
    icon: ChatTeardropTextIcon,
    kind: `message_created`,
    data: {
      conditions: [field(`messageChannel`, `sms`), field(`messageDirection`, `inbound`)],
      match: `all`,
    },
  },
  {
    id: `sms_sent`,
    label: `SMS sent`,
    summary: `Any text goes out to a contact (staff, AI, or workflow)`,
    icon: PaperPlaneTiltIcon,
    kind: `message_created`,
    data: {
      conditions: [field(`messageChannel`, `sms`), field(`messageDirection`, `outbound`)],
      match: `all`,
    },
  },
  {
    id: `pipeline_stage_changed`,
    label: copy.movedToListStage,
    summary: copy.contactMovesIntoListStage,
    icon: FunnelIcon,
    // Contact state lane. The stage is org-specific, so we prefill an empty pipeline-stage condition
    // for the user to complete. The stage picker writes the composite `<pipelineId>::<stage>` that
    // actually enrolls (TRK-5495, live 08-14); only a stage saved before that change still gates.
    kind: `contact_updated`,
    data: { conditions: [{ type: `field`, field: `pipelineStage`, operator: `is` }], match: `all`, source: `any` },
  },
  // Presets name a moment only; which treatment and how much is the author's call.
  ...TREATMENT_MILESTONES.map(
    (milestone): TriggerPreset => ({
      id: `treatment_${milestone.value}`,
      label: milestone.label,
      summary: milestone.summary,
      icon: ToothIcon,
      kind: `treatment_plan_updated`,
      data: { treatment: { milestone: milestone.value }, requireContactMatch: true },
    }),
  ),
]

/** There are only ever two of these (one per wording, and `listCopy` hands back the same two objects
 *  every time), so the list is built once per wording rather than on every label lookup. */
const presetsByCopy = new Map<ListCopy, TriggerPreset[]>()

export function triggerPresets(copy: ListCopy): TriggerPreset[] {
  const cached = presetsByCopy.get(copy)
  if (cached) return cached
  const built = buildTriggerPresets(copy)
  presetsByCopy.set(copy, built)
  return built
}

// ————————————————————————————————————————————————————————————————————————————————————————————————
// Picker rows — the one flat list the trigger picker shows (design language, 2026-09-02).

export type TriggerPickerRow = {
  /** Preset id, or `raw:<kind>` for a raw object event. */
  id: string
  label: string
  /** Goes behind the row's "i"; never rendered as a subtitle. */
  detail: string
  icon: Icon
  kind: TriggerKind
  data?: TriggerPresetData
  status: TriggerStatus
  group: string
  /** A conditioned preset: a specific moment, listed above its group's other triggers. */
  named: boolean
}

/** One list for the trigger picker, grouped by object, each group running from specific to broad:
 *  conditioned presets ("Missed call" under Call, "Broken appointment" under Appointment), then the object's
 *  events, then its "Any ..." catch-all. A preset with no conditions is a pure alias of its raw event,
 *  so it takes the raw row's place ("Form submitted" never appears twice). Manual last. */
export function triggerPickerRows(copy: ListCopy): TriggerPickerRow[] {
  const presets = triggerPresets(copy)
  const isMoment = (preset: TriggerPreset) => !!(preset.data.conditions?.length || preset.data.treatment?.milestone)
  const aliasByKind = new Map(presets.filter((preset) => !isMoment(preset)).map((preset) => [preset.kind, preset]))
  const presetRow = (preset: TriggerPreset, group: string, named: boolean): TriggerPickerRow => ({
    id: preset.id,
    label: preset.label,
    detail: preset.summary,
    icon: preset.icon,
    kind: preset.kind,
    data: preset.data,
    status: CATALOG_BY_KIND[preset.kind]?.status ?? `live`,
    group,
    named,
  })
  return triggerCatalogByObject().flatMap((group) => [
    ...presets
      .filter((preset) => isMoment(preset) && CATALOG_BY_KIND[preset.kind]?.object === group.object)
      .map((preset) => presetRow(preset, group.label, true)),
    ...[...group.defs.filter((def) => !def.catchAll), ...group.defs.filter((def) => def.catchAll)].map(
      (def): TriggerPickerRow => {
        const alias = aliasByKind.get(def.kind)
        if (alias) return presetRow(alias, group.label, false)
        return {
          id: `raw:${def.kind}`,
          label: def.label,
          detail: def.summary,
          icon: def.icon,
          kind: def.kind,
          status: def.status,
          group: group.label,
          named: false,
        }
      },
    ),
  ])
}

/** Every term must appear in the label, detail, or object heading, so "call missed" narrows. */
export function triggerPickerSearch<T extends TriggerPickerRow>(rows: T[], query: string): T[] {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean)
  if (terms.length === 0) return rows
  return rows.filter((row) => {
    const text = `${row.label} ${row.detail} ${row.group}`.toLowerCase()
    return terms.every((term) => text.includes(term))
  })
}

// ————————————————————————————————————————————————————————————————————————————————————————————————
// Trigger names. A named moment ("Became inactive") is a front door, not an identity: the name
// holds only while the moment's own conditions are still intact, and falls back to the honest event
// label the instant a defining condition is changed or removed. A typed-in name overrides both.

/** The slice of trigger node data the naming rules read; structural to avoid a types.ts cycle. */
type TriggerNameInput = {
  kind: TriggerKind
  label?: string
  conditions?: readonly Condition[]
  treatment?: { milestone?: string }
}

/** A preset condition is "still present" when a node condition matches its subject and operator and,
 *  where the preset fixes a value, that value. Presets that leave the value open (pipeline stage)
 *  match any value the user filled in. Scope is ignored: the editor adds it, the preset omits it. */
function holdsCondition(conditions: readonly Condition[], wanted: Condition): boolean {
  return conditions.some((have) => {
    if (have.type !== wanted.type) return false
    if (have.type === `event` && wanted.type === `event`) return have.event === wanted.event
    if (have.type === `field` && wanted.type === `field`)
      return (
        have.field === wanted.field &&
        have.operator === wanted.operator &&
        (wanted.value === undefined || have.value === wanted.value)
      )
    return false
  })
}

/** The name the node earns from its configuration: the first named moment whose defining conditions
 *  it still carries, else the raw event's picker label. */
export function inferredTriggerLabel(data: TriggerNameInput, copy: ListCopy): string {
  const conditions = data.conditions ?? []
  const moment = triggerPresets(copy).find(
    (preset) =>
      preset.kind === data.kind &&
      (!preset.data.treatment?.milestone || preset.data.treatment.milestone === data.treatment?.milestone) &&
      (preset.data.conditions ?? []).every((wanted) => holdsCondition(conditions, wanted)),
  )
  return moment?.label ?? triggerLabel(data.kind)
}

/** Names the builder wrote before a trigger was renamed to say exactly what it enrolls. */
const RETIRED_TRIGGER_LABELS = [
  `Contact updated`,
  `Form updated`,
  `Call received`,
  `Activity updated`,
  `Message sent or received`,
  `Appointment updated`,
  `Treatment plan changed`,
  `Task updated`,
  `No-show`,
]

/** Every name the builder itself can write into `label`. A stored label outside this set was typed
 *  by a person, so it wins; one inside it is a snapshot from the picker and re-derives freely.
 *
 *  Both wordings of the list/pipeline moment are in here: a workflow saved before `grouped-navigation`
 *  carries the pipeline snapshot, and that has to re-derive rather than read as a name somebody chose. */
const BUILDER_TRIGGER_LABELS = new Set<string>([
  ...triggerPresets(listCopy(false)).map((preset) => preset.label),
  ...triggerPresets(listCopy(true)).map((preset) => preset.label),
  ...TRIGGER_CATALOG.map((def) => def.label),
  ...RETIRED_TRIGGER_LABELS,
])

/** What the card and the inspector header show: a typed name wins, a picker snapshot re-derives. */
export function triggerDisplayLabel(data: TriggerNameInput, copy: ListCopy): string {
  const label = data.label?.trim()
  return label && !BUILDER_TRIGGER_LABELS.has(label) ? label : inferredTriggerLabel(data, copy)
}
